import axios from 'axios';
import diagnosticsCollector from './diagnosticsCollector';

// API Base URL - reads from environment variable
// Development: http://localhost:5120/api (from .env)
// Production: https://api.ktc.moe.gov.my/api (from .env.production)
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5120/api';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include JWT token and track API calls
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Track API call start time for diagnostics
    config.metadata = { startTime: new Date() };

    return config;
  },
  (error) => Promise.reject(error)
);

// Track if we're currently refreshing the token to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Add response interceptor to handle errors and track API call history
apiClient.interceptors.response.use(
  (response) => {
    // Track successful API call
    const duration = new Date() - response.config.metadata.startTime;
    diagnosticsCollector.addApiCall({
      method: response.config.method.toUpperCase(),
      url: response.config.url,
      status: response.status,
      duration: duration + 'ms',
      success: true
    });

    return response;
  },
  async (error) => {
    // Track failed API call
    if (error.config?.metadata?.startTime) {
      const duration = new Date() - error.config.metadata.startTime;
      diagnosticsCollector.addApiCall({
        method: error.config.method.toUpperCase(),
        url: error.config.url,
        status: error.response?.status || 0,
        duration: duration + 'ms',
        success: false,
        error: error.message
      });
    }

    const originalRequest = error.config;

    // For blob requests (file downloads), don't auto-redirect - let the calling code handle errors
    if (originalRequest?.responseType === 'blob') {
      return Promise.reject(error);
    }

    // If error is 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't try to refresh if the failed request was the refresh or login endpoint itself
      if (originalRequest.url === '/auth/refresh' || originalRequest.url === '/auth/login') {
        // Refresh token is invalid or expired - logout user
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If already refreshing, queue this request to retry after refresh completes
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return apiClient(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        // No refresh token available - logout user
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        // Call refresh endpoint
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refreshToken: refreshToken
        });

        if (response.data.success && response.data.data) {
          const { accessToken, refreshToken: newRefreshToken } = response.data.data;

          // Update tokens in localStorage
          localStorage.setItem('accessToken', accessToken);
          if (newRefreshToken) {
            localStorage.setItem('refreshToken', newRefreshToken);
          }

          // Update default authorization header
          apiClient.defaults.headers.common['Authorization'] = 'Bearer ' + accessToken;
          originalRequest.headers['Authorization'] = 'Bearer ' + accessToken;

          // Process queued requests
          processQueue(null, accessToken);

          // Retry original request
          return apiClient(originalRequest);
        } else {
          // Refresh failed - logout user
          processQueue(error, null);
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return Promise.reject(error);
        }
      } catch (refreshError) {
        // Refresh failed - logout user
        processQueue(refreshError, null);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  login: async (email, password) => {
    const response = await apiClient.post('/auth/login', { email, password });
    return response.data;
  },

  logout: async () => {
    const response = await apiClient.post('/auth/logout');
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },
};

// Dashboard API
export const dashboardAPI = {
  getDashboard: async () => {
    const response = await apiClient.get('/dashboard');
    return response.data;
  },
};

// PPD Dashboard API
export const ppdDashboardAPI = {
  getPPDDashboard: async (activityId = null, ppdCode = null) => {
    const params = {};
    if (activityId) params.activityId = activityId;
    if (ppdCode) params.ppdCode = ppdCode;
    const response = await apiClient.get('/dashboard/ppd', { params });
    return response.data;
  },
  reopenSchool: async (schoolCode, activityId) => {
    const response = await apiClient.post('/dashboard/ppd/reopen-school', {
      schoolCode,
      activityId
    });
    return response.data;
  },
};

// Admin Dashboard API
export const adminDashboardAPI = {
  getAdminDashboard: async (activityId = null) => {
    const params = activityId ? { activityId } : {};
    const response = await apiClient.get('/dashboard/admin', { params });
    return response.data;
  },
};

// Authorization API
export const authorizationAPI = {
  // Get permissions for a specific resource
  getPermissions: async (resourceCode) => {
    const response = await apiClient.get(`/Authorization/permissions?resourceCode=${encodeURIComponent(resourceCode)}`);
    return response.data;
  },

  // Get all accessible resources for current user
  getAccessibleResources: async () => {
    const response = await apiClient.get('/Authorization/accessible-resources');
    return response.data;
  },
};

// User Profile API (ProfileController)
export const profileAPI = {
  // Personal Profile
  getProfile: async () => {
    const response = await apiClient.get('/profile');
    return response.data;
  },

  updateProfile: async (data) => {
    const response = await apiClient.put('/profile', data);
    return response.data;
  },

  changePassword: async (data) => {
    const response = await apiClient.post('/profile/change-password', data);
    return response.data;
  },

  // User Management (Admin/Headmaster only)
  getSchoolUsers: async () => {
    const response = await apiClient.get('/profile/users');
    return response.data;
  },

  createUser: async (data) => {
    const response = await apiClient.post('/profile/users', data);
    return response.data;
  },

  resetPassword: async (data) => {
    const response = await apiClient.post('/profile/users/reset-password', data);
    return response.data;
  },

  enableUser: async (userId) => {
    const response = await apiClient.put(`/profile/users/${userId}/enable`);
    return response.data;
  },

  disableUser: async (userId) => {
    const response = await apiClient.put(`/profile/users/${userId}/disable`);
    return response.data;
  },

  getAvailableRoles: async () => {
    const response = await apiClient.get('/profile/roles');
    return response.data;
  },
};

// Student Uniform API (StudentUniformController)
export const studentUniformAPI = {
  // Get activities for the school
  getActivities: async () => {
    const response = await apiClient.get('/studentuniform/activities');
    return response.data;
  },

  // Get tahuns (grades) for the school
  getTahuns: async () => {
    const response = await apiClient.get('/studentuniform/tahuns');
    return response.data;
  },

  // Get size configuration for an activity
  getSizeConfig: async (activityId) => {
    const response = await apiClient.get(`/studentuniform/size-config/${activityId}`);
    return response.data;
  },

  // Get students with uniform data
  getStudents: async (activityId, tahun) => {
    const response = await apiClient.get('/studentuniform/students', {
      params: { activityId, tahun }
    });
    return response.data;
  },

  // Get school information for header
  getSchoolInfo: async () => {
    const response = await apiClient.get('/studentuniform/school-info');
    return response.data;
  },

  // Get submission status
  getStatus: async (activityId, tahun) => {
    const response = await apiClient.get('/studentuniform/status', {
      params: { activityId, tahun }
    });
    return response.data;
  },

  // Save uniform data (draft mode)
  saveUniform: async (data) => {
    const response = await apiClient.post('/studentuniform/save', data);
    return response.data;
  },

  // Submit uniform data (final submission)
  submitUniform: async (data) => {
    const response = await apiClient.post('/studentuniform/submit', data);
    return response.data;
  },

  // Import students with validation
  importStudents: async (data) => {
    const response = await apiClient.post('/studentuniform/import', data);
    return response.data;
  },

  // Get Tahun list (grades) for dropdown - from SCHOOL_TAHUN table
  getTahunList: async () => {
    const response = await apiClient.get('/studentuniform/tahun-list');
    return response.data;
  },

  // Get Kelas list (classes) for dropdown - from SCHOOL_CLASS table filtered by school
  getKelasList: async () => {
    const response = await apiClient.get('/studentuniform/kelas-list');
    return response.data;
  },

  // Lookup student by IC number - for auto-filling student details when adding new rows
  lookupStudent: async (ic, enrollYear = null) => {
    const params = { ic };
    if (enrollYear) {
      params.enrollYear = enrollYear;
    }
    const response = await apiClient.get('/studentuniform/lookup-student', {
      params
    });
    return response.data;
  },
};

// Activity Management API (ActivityController)
export const activityAPI = {
  // Get all activities with pagination and optional search
  getActivities: async (page = 1, pageSize = 50, searchTerm = null) => {
    const params = { page, pageSize };
    if (searchTerm) params.searchTerm = searchTerm;
    const response = await apiClient.get('/activity', { params });
    return response.data;
  },

  // Get activity by ID
  getActivity: async (id) => {
    const response = await apiClient.get(`/activity/${id}`);
    return response.data;
  },

  // Create new activity
  createActivity: async (data) => {
    const response = await apiClient.post('/activity', data);
    return response.data;
  },

  // Update existing activity
  updateActivity: async (id, data) => {
    const response = await apiClient.put(`/activity/${id}`, data);
    return response.data;
  },

  // Delete activity
  deleteActivity: async (id) => {
    const response = await apiClient.delete(`/activity/${id}`);
    return response.data;
  },

  // Get size configuration for an activity
  getSizeConfig: async (activityId) => {
    const response = await apiClient.get(`/activity/${activityId}/size-config`);
    return response.data;
  },

  // Update size configuration for an activity
  updateSizeConfig: async (activityId, data) => {
    const response = await apiClient.put(`/activity/${activityId}/size-config`, data);
    return response.data;
  },

  // Get schools mapped to an activity
  getActivitySchools: async (activityId, page = 1, pageSize = 50, searchTerm = null, filterPpd = null) => {
    const params = { page, pageSize, ...(searchTerm && { searchTerm }), ...(filterPpd && { filterPpd }) };
    const response = await apiClient.get(`/activity/${activityId}/schools`, { params });
    return response.data;
  },

  // Get available schools not yet mapped to an activity
  getAvailableSchools: async (activityId) => {
    const response = await apiClient.get(`/activity/${activityId}/available-schools`);
    return response.data;
  },

  // Map a school to an activity
  mapSchoolToActivity: async (activityId, data) => {
    const response = await apiClient.post(`/activity/${activityId}/schools`, data);
    return response.data;
  },

  // Remove school from activity mapping
  removeSchoolFromActivity: async (activitySchoolId) => {
    const response = await apiClient.delete(`/activity/schools/${activitySchoolId}`);
    return response.data;
  },

  // Update school eligibility for an activity
  updateSchoolEligibility: async (activitySchoolId, isEligible) => {
    const response = await apiClient.put(`/activity/schools/${activitySchoolId}/eligibility`, { isEligible });
    return response.data;
  },

  // Bulk map all schools to an activity
  bulkMapAllSchools: async (activityId, data) => {
    const response = await apiClient.post(`/activity/${activityId}/bulk-map/all`, data);
    return response.data;
  },

  // Bulk map schools by PPD to an activity
  bulkMapByPpd: async (activityId, data) => {
    const response = await apiClient.post(`/activity/${activityId}/bulk-map/ppd`, data);
    return response.data;
  },

  // Bulk map schools by DUN to an activity
  bulkMapByDun: async (activityId, data) => {
    const response = await apiClient.post(`/activity/${activityId}/bulk-map/dun`, data);
    return response.data;
  },

  // Bulk map schools by Parliament to an activity
  bulkMapByPar: async (activityId, data) => {
    const response = await apiClient.post(`/activity/${activityId}/bulk-map/par`, data);
    return response.data;
  },
};

// DUN Management API
export const dunAPI = {
  // Get all DUNs with optional search
  getDuns: async (searchTerm = null) => {
    const params = {};
    if (searchTerm) params.searchTerm = searchTerm;
    const response = await apiClient.get('/dun', { params });
    return response.data;
  },

  // Get DUN by code
  getDun: async (code) => {
    const response = await apiClient.get(`/dun/${code}`);
    return response.data;
  },

  // Create new DUN
  createDun: async (data) => {
    const response = await apiClient.post('/dun', data);
    return response.data;
  },

  // Update existing DUN
  updateDun: async (code, data) => {
    const response = await apiClient.put(`/dun/${code}`, data);
    return response.data;
  },

  // Delete DUN
  deleteDun: async (code) => {
    const response = await apiClient.delete(`/dun/${code}`);
    return response.data;
  },
};

// PAR Management API
export const parAPI = {
  // Get all PARs with optional search
  getPars: async (searchTerm = null) => {
    const params = {};
    if (searchTerm) params.searchTerm = searchTerm;
    const response = await apiClient.get('/par', { params });
    return response.data;
  },

  // Get PAR by code
  getPar: async (code) => {
    const response = await apiClient.get(`/par/${code}`);
    return response.data;
  },

  // Create new PAR
  createPar: async (data) => {
    const response = await apiClient.post('/par', data);
    return response.data;
  },

  // Update existing PAR
  updatePar: async (code, data) => {
    const response = await apiClient.put(`/par/${code}`, data);
    return response.data;
  },

  // Delete PAR
  deletePar: async (code) => {
    const response = await apiClient.delete(`/par/${code}`);
    return response.data;
  },
};

// School Class Management API
export const schoolClassAPI = {
  // Get all school classes with optional filters
  getSchoolClasses: async (schoolCode = null, searchTerm = null) => {
    const params = {};
    if (schoolCode) params.schoolCode = schoolCode;
    if (searchTerm) params.searchTerm = searchTerm;
    const response = await apiClient.get('/schoolclass', { params });
    return response.data;
  },

  // Get school class by ID
  getSchoolClass: async (id) => {
    const response = await apiClient.get(`/schoolclass/${id}`);
    return response.data;
  },

  // Create new school class
  createSchoolClass: async (data) => {
    const response = await apiClient.post('/schoolclass', data);
    return response.data;
  },

  // Update existing school class
  updateSchoolClass: async (id, data) => {
    const response = await apiClient.put(`/schoolclass/${id}`, data);
    return response.data;
  },

  // Delete school class
  deleteSchoolClass: async (id) => {
    const response = await apiClient.delete(`/schoolclass/${id}`);
    return response.data;
  },
};

// School Management API
export const schoolAPI = {
  // Get all schools with optional search and filtering
  getSchools: async (searchTerm = null, filterPpd = null) => {
    const params = {};
    if (searchTerm) params.searchTerm = searchTerm;
    if (filterPpd) params.filterPpd = filterPpd;
    const response = await apiClient.get('/school', { params });
    return response.data;
  },

  // Get school by code
  getSchool: async (code) => {
    const response = await apiClient.get(`/school/${code}`);
    return response.data;
  },

  // Create new school
  createSchool: async (data) => {
    const response = await apiClient.post('/school', data);
    return response.data;
  },

  // Update existing school
  updateSchool: async (code, data) => {
    const response = await apiClient.put(`/school/${code}`, data);
    return response.data;
  },

  // Delete school
  deleteSchool: async (code) => {
    const response = await apiClient.delete(`/school/${code}`);
    return response.data;
  },
};

// Tahun Management API
export const tahunAPI = {
  // Get all tahun with optional search
  getTahuns: async (searchTerm = null) => {
    const params = {};
    if (searchTerm) params.searchTerm = searchTerm;
    const response = await apiClient.get('/tahun', { params });
    return response.data;
  },

  // Get tahun by ID
  getTahun: async (id) => {
    const response = await apiClient.get(`/tahun/${id}`);
    return response.data;
  },

  // Create new tahun
  createTahun: async (data) => {
    const response = await apiClient.post('/tahun', data);
    return response.data;
  },

  // Update existing tahun
  updateTahun: async (id, data) => {
    const response = await apiClient.put(`/tahun/${id}`, data);
    return response.data;
  },

  // Delete tahun
  deleteTahun: async (id) => {
    const response = await apiClient.delete(`/tahun/${id}`);
    return response.data;
  },
};

// School Enrollment Year Management API
export const schoolEnrolYearAPI = {
  // Get all school enrollment years with optional search, filtering and pagination
  getSchoolEnrolYears: async (page = 1, pageSize = 50, searchTerm = null, enrolYear = null, status = null, ppdDistrict = null) => {
    const params = { page, pageSize };
    if (searchTerm) params.searchTerm = searchTerm;
    if (enrolYear) params.enrolYear = enrolYear;
    if (status) params.status = status;
    if (ppdDistrict) params.ppdDistrict = ppdDistrict;
    const response = await apiClient.get('/schoolenrolyear', { params });
    return response.data;
  },

  // Get school enrollment year by ID
  getSchoolEnrolYear: async (id) => {
    const response = await apiClient.get(`/schoolenrolyear/${id}`);
    return response.data;
  },

  // Get all enrollment years for a specific school
  getSchoolEnrolYearsBySchool: async (schoolCode) => {
    const response = await apiClient.get(`/schoolenrolyear/school/${schoolCode}`);
    return response.data;
  },

  // Create new school enrollment year
  createSchoolEnrolYear: async (data) => {
    const response = await apiClient.post('/schoolenrolyear', data);
    return response.data;
  },

  // Update existing school enrollment year
  updateSchoolEnrolYear: async (id, data) => {
    const response = await apiClient.put(`/schoolenrolyear/${id}`, data);
    return response.data;
  },

  // Delete school enrollment year
  deleteSchoolEnrolYear: async (id) => {
    const response = await apiClient.delete(`/schoolenrolyear/${id}`);
    return response.data;
  },

  // Bulk create enrollment years for multiple schools
  bulkCreateSchoolEnrolYear: async (data) => {
    const response = await apiClient.post('/schoolenrolyear/bulk-create', data);
    return response.data;
  },

  // Bulk update status for multiple enrollment years
  bulkUpdateStatus: async (data) => {
    const response = await apiClient.post('/schoolenrolyear/bulk-update-status', data);
    return response.data;
  },

  // Get list of available enrollment years (for dropdowns)
  getAvailableYears: async () => {
    const response = await apiClient.get('/schoolenrolyear/available-years');
    return response.data;
  },
};

// User Management API
export const userManagementAPI = {
  // Get all users with optional search and filtering
  getUsers: async (searchTerm = null, roleFilter = null, statusFilter = null, districtFilter = null, schoolFilter = null) => {
    const params = {};
    if (searchTerm) params.searchTerm = searchTerm;
    if (roleFilter) params.roleFilter = roleFilter;
    if (statusFilter) params.statusFilter = statusFilter;
    if (districtFilter) params.districtFilter = districtFilter;
    if (schoolFilter) params.schoolFilter = schoolFilter;
    const response = await apiClient.get('/usermanagement', { params });
    return response.data;
  },

  // Get user by ID
  getUser: async (id) => {
    const response = await apiClient.get(`/usermanagement/${id}`);
    return response.data;
  },

  // Create new user
  createUser: async (data) => {
    const response = await apiClient.post('/usermanagement', data);
    return response.data;
  },

  // Update existing user
  updateUser: async (id, data) => {
    const response = await apiClient.put(`/usermanagement/${id}`, data);
    return response.data;
  },

  // Delete user
  deleteUser: async (id) => {
    const response = await apiClient.delete(`/usermanagement/${id}`);
    return response.data;
  },

  // Reset user password
  resetUserPassword: async (userId, data) => {
    const response = await apiClient.post(`/usermanagement/${userId}/reset-password`, data);
    return response.data;
  },

  // Get permissions for current user
  getPermissions: async () => {
    const response = await apiClient.get('/usermanagement/permissions');
    return response.data;
  },

  // Get list of districts (ADMIN/SUPERADMIN only)
  getDistricts: async () => {
    const response = await apiClient.get('/usermanagement/districts');
    return response.data;
  },

  // Get list of schools with optional district filter
  getSchools: async (district = null) => {
    const params = {};
    if (district) params.district = district;
    const response = await apiClient.get('/usermanagement/schools', { params });
    return response.data;
  },
};

// Report API
export const reportAPI = {
  getSchoolMapData: async (ppd, school) => {
    const params = {};
    if (ppd) params.ppd = ppd;
    if (school) params.school = school;
    const response = await apiClient.get('/reports/school-map', { params });
    return response.data?.data || response.data; // Handle ApiResponse wrapper
  },

  getSchoolMapPPDs: async () => {
    const response = await apiClient.get('/reports/school-map/filter-options/ppds');
    return response.data?.data || response.data;
  },

  getSchoolMapSchools: async (ppd) => {
    const params = {};
    if (ppd) params.ppd = ppd;
    const response = await apiClient.get('/reports/school-map/filter-options/schools', { params });
    return response.data?.data || response.data;
  },
};

// Issue Reports API
export const issueReportsAPI = {
  // Create new issue report
  createIssueReport: async (data) => {
    const response = await apiClient.post('/issuereports', data);
    return response.data;
  },

  // Get issue reports with filtering
  getIssueReports: async (filter) => {
    const params = new URLSearchParams();
    Object.keys(filter).forEach(key => {
      if (filter[key] !== undefined && filter[key] !== null && filter[key] !== '') {
        params.append(key, filter[key]);
      }
    });

    const response = await apiClient.get(`/issuereports?${params.toString()}`);
    return response.data;
  },

  // Get issue report details
  getIssueReport: async (id) => {
    const response = await apiClient.get(`/issuereports/${id}`);
    return response.data;
  },

  // Update issue status
  updateStatus: async (id, data) => {
    const response = await apiClient.put(`/issuereports/${id}/status`, data);
    return response.data;
  },

  // Send message to issue report
  sendMessage: async (id, message) => {
    const response = await apiClient.post(`/issuereports/${id}/messages`, message);
    return response.data;
  },

  // Get messages for issue report
  getMessages: async (id) => {
    const response = await apiClient.get(`/issuereports/${id}/messages`);
    return response.data;
  },

  // Assign issue to role
  assignIssue: async (id, role) => {
    const response = await apiClient.put(`/issuereports/${id}/assign`, { assignedToRole: role });
    return response.data;
  },

  // Mark issue as read
  markAsRead: async (id) => {
    const response = await apiClient.post(`/issuereports/${id}/read`);
    return response.data;
  },

  // Get unread count
  getUnreadCount: async () => {
    const response = await apiClient.get('/issuereports/unread-count');
    return response.data;
  },

  // Get statistics
  getStatistics: async () => {
    const response = await apiClient.get('/issuereports/statistics');
    return response.data;
  }
};

// Student Master API
export const studentMasterAPI = {
  // Get students with pagination and filtering
  getStudents: async (filters) => {
    const response = await apiClient.get('/studentmaster', { params: filters });
    return response.data;
  },

  // Get student by ID
  getStudent: async (id) => {
    const response = await apiClient.get(`/studentmaster/${id}`);
    return response.data;
  },

  // Create new student
  createStudent: async (data) => {
    const response = await apiClient.post('/studentmaster', data);
    return response.data;
  },

  // Update existing student (using POST instead of PUT to avoid WebDAV-Redirector issues)
  updateStudent: async (id, data) => {
    const response = await apiClient.post(`/studentmaster/${id}`, data);
    return response.data;
  },

  // Get student enrollments (for delete confirmation)
  getStudentEnrollments: async (id) => {
    const response = await apiClient.get(`/studentmaster/${id}/enrollments`);
    return response.data;
  },

  // Delete student enrollment (soft delete with reason)
  deleteStudent: async (id, enrollYear, reason) => {
    const response = await apiClient.delete(`/studentmaster/${id}?enrollYear=${enrollYear}&reason=${encodeURIComponent(reason)}`);
    return response.data;
  },

  // Bulk delete students
  bulkDeleteStudents: async (ids) => {
    const response = await apiClient.post('/studentmaster/bulk-delete', { studentIds: ids });
    return response.data;
  },

  // Export students to Excel (server-side)
  exportStudents: async (filters) => {
    const response = await apiClient.post('/studentmaster/export-excel', filters, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Get Tahun list (grades) for dropdown
  getTahunList: async () => {
    const response = await apiClient.get('/studentmaster/tahun-list');
    return response.data;
  },

  // Get Kelas list (classes) for dropdown
  getKelasList: async () => {
    const response = await apiClient.get('/studentmaster/kelas-list');
    return response.data;
  },

  // Validate IC number
  validateIC: async (requestData) => {
    const response = await apiClient.post('/studentmaster/validate-ic', requestData);
    return response.data;
  },

  // Get PPD list for dropdown (SUPERADMIN only)
  getPPDs: async () => {
    const response = await apiClient.get('/studentmaster/ppds');
    return response.data;
  },

  // Get schools list for dropdown (filtered by PPD if provided)
  getSchools: async (ppdCode = null) => {
    const params = ppdCode ? { ppd: ppdCode } : {};
    const response = await apiClient.get('/studentmaster/schools', { params });
    return response.data;
  },

  // Get enrollment years for dropdown
  getEnrollmentYears: async (schoolCode = null) => {
    const params = schoolCode ? { schoolCode } : {};
    const response = await apiClient.get('/studentmaster/enrollment-years', { params });
    return response.data;
  },

  // Get Tahuns (grades) for dropdown (filtered by school if provided)
  getTahuns: async (schoolCode = null) => {
    const params = schoolCode ? { schoolCode } : {};
    const response = await apiClient.get('/studentmaster/tahuns', { params });
    return response.data;
  },

  // Get Kelas (classes) for dropdown (filtered by school and tahun if provided)
  getKelas: async (schoolCode = null, tahun = null) => {
    const params = {};
    if (schoolCode) params.schoolCode = schoolCode;
    if (tahun) params.tahun = tahun;
    const response = await apiClient.get('/studentmaster/kelas', { params });
    return response.data;
  },

  // Get student details by ID
  getStudentDetails: async (studentId) => {
    const response = await apiClient.get(`/studentmaster/${studentId}/details`);
    return response.data;
  },

  // Hierarchy endpoints for lazy loading

  // Get PPD hierarchy (SUPERADMIN only)
  getPPDHierarchy: async (filters) => {
    const response = await apiClient.get('/studentmaster/hierarchy/ppds', { params: filters });
    return response.data;
  },

  // Get schools for PPD (PPD role or lazy loading for SUPERADMIN)
  getSchoolsForPPD: async (ppdCode, filters) => {
    const response = await apiClient.get(`/studentmaster/hierarchy/ppd/${ppdCode}/schools`, { params: filters });
    return response.data;
  },

  // Get tahuns for school (lazy loading)
  getTahunsForSchool: async (schoolCode, filters) => {
    const response = await apiClient.get(`/studentmaster/hierarchy/school/${schoolCode}/tahuns`, { params: filters });
    return response.data;
  },

  // Get kelas for school and tahun (lazy loading)
  getKelasForSchoolTahun: async (schoolCode, tahun, filters) => {
    const response = await apiClient.get(`/studentmaster/hierarchy/school/${schoolCode}/tahun/${tahun}/kelas`, { params: filters });
    return response.data;
  },

  // Get students for kelas (lazy loading)
  getStudentsForKelas: async (schoolCode, tahun, kelas, filters) => {
    const response = await apiClient.get(`/studentmaster/hierarchy/school/${schoolCode}/tahun/${tahun}/kelas/${kelas}/students`, { params: filters });
    return response.data;
  },

  // Get student hierarchy (HEADMASTER/TEACHER - old non-lazy method)
  getStudentHierarchy: async (filters) => {
    const response = await apiClient.get('/studentmaster/hierarchy', { params: filters });
    return response.data;
  },

  // Get current enrollment year
  getCurrentEnrollmentYear: async () => {
    const response = await apiClient.get('/studentmaster/current-enrollment-year');
    return response.data;
  },

  // Update student enrollment (Tahun/Kelas) - using POST to avoid WebDAV-Redirector issues
  updateEnrollment: async (studentId, data) => {
    const response = await apiClient.post(`/studentmaster/${studentId}/enrollment`, data);
    return response.data;
  },

  // Update enrollment status (ACTIVE/INACTIVE with reason) - using POST to avoid WebDAV-Redirector issues
  updateEnrollmentStatus: async (studentId, data) => {
    const response = await apiClient.post(`/studentmaster/${studentId}/enrollment/status`, data);
    return response.data;
  }
};

// Report Summary API
export const reportSummaryAPI = {
  getActivities: async () => {
    const response = await apiClient.get('/reportsummary/activities');
    return response.data;
  },
  getYears: async () => {
    const response = await apiClient.get('/reportsummary/years');
    return response.data;
  },
  getPPDs: async () => {
    const response = await apiClient.get('/reportsummary/ppds');
    return response.data;
  },
  getSchools: async (ppdCode) => {
    const response = await apiClient.get('/reportsummary/schools', { params: { ppdCode } });
    return response.data;
  },
  getSummaryData: async (activityId, ppdCode, schoolCode, year) => {
    const params = { activityId, ppdCode, schoolCode, enrollYear: year };
    const response = await apiClient.get('/reportsummary/data', { params });
    return response.data;
  },
  exportToExcel: async (exportRequest) => {
    const response = await apiClient.post('/reportsummary/export-excel', exportRequest, {
      responseType: 'blob'
    });

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Summary_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  },
  exportToPdf: async (exportRequest) => {
    const response = await apiClient.post('/reportsummary/export-pdf', exportRequest, {
      responseType: 'blob'
    });

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Summary_Report_${new Date().toISOString().slice(0, 10)}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  }
};

// Report School API
export const reportSchoolAPI = {
  getActivities: async (schoolCode) => {
    const response = await apiClient.get('/reportschool/activities', { params: { schoolCode } });
    return response.data;
  },
  getPPDs: async () => {
    const response = await apiClient.get('/reportschool/ppds');
    return response.data;
  },
  getSchools: async (ppdCode) => {
    const response = await apiClient.get('/reportschool/schools', { params: { ppdCode } });
    return response.data;
  },
  getReportData: async (schoolCode, activityId) => {
    const response = await apiClient.get('/reportschool/data', { params: { schoolCode, activityId } });
    return response.data;
  },
  submitSchool: async (data) => {
    const response = await apiClient.post('/reportschool/submit', data);
    return response.data;
  },
  getGradeStatus: async (schoolCode, activityId) => {
    const response = await apiClient.get('/reportschool/grade-status', { params: { schoolCode, activityId } });
    return response.data;
  },
  updateGradeStatus: async (data) => {
    const response = await apiClient.post('/reportschool/update-grade-status', data);
    return response.data;
  },
  exportToExcel: async (exportRequest) => {
    const response = await apiClient.post('/reportschool/export-excel', exportRequest, {
      responseType: 'blob'
    });

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `School_Report_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  },
  exportToPdf: async (exportRequest) => {
    const response = await apiClient.post('/reportschool/export-pdf', exportRequest, {
      responseType: 'blob'
    });

    const blob = new Blob([response.data], {
      type: 'application/pdf'
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `School_Report_${new Date().toISOString().slice(0, 10)}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  }
};

// Report Student Listing API
export const reportStudentListingAPI = {
  getActivities: async (schoolCode) => {
    const response = await apiClient.get('/reportstudentlisting/activities', { params: { schoolCode } });
    return response.data;
  },
  getPPDs: async () => {
    const response = await apiClient.get('/reportstudentlisting/ppds');
    return response.data;
  },
  getSchools: async (ppdCode) => {
    const response = await apiClient.get('/reportstudentlisting/schools', { params: { ppdCode } });
    return response.data;
  },
  getSchoolInfo: async (schoolCode) => {
    const response = await apiClient.get('/reportstudentlisting/school-info', { params: { schoolCode } });
    return response.data;
  },
  getReportData: async (schoolCode, activityId, year) => {
    const response = await apiClient.get('/reportstudentlisting/data', {
      params: { schoolCode, activityId, year }
    });
    return response.data;
  },
  exportToExcel: async (exportRequest) => {
    try {
      const response = await apiClient.post('/reportstudentlisting/export-excel', exportRequest, {
        responseType: 'blob'
      });

      // Check if the response is actually an error (JSON error response as blob)
      const contentType = response.headers['content-type'];
      if (contentType && contentType.includes('application/json')) {
        // Server returned JSON error, parse it
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.message || 'Export failed');
      }

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Student_Listing_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      // Handle blob error responses
      if (error.response && error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          throw new Error(errorData.message || 'Export failed');
        } catch (parseError) {
          // If parsing fails, throw original error
          throw error;
        }
      }
      throw error;
    }
  },
  exportToPdf: async (exportRequest) => {
    try {
      const response = await apiClient.post('/reportstudentlisting/export-pdf', exportRequest, {
        responseType: 'blob'
      });

      // Check if the response is actually an error (JSON error response as blob)
      const contentType = response.headers['content-type'];
      if (contentType && contentType.includes('application/json')) {
        // Server returned JSON error, parse it
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.message || 'Export failed');
      }

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Student_Listing_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      // Handle blob error responses
      if (error.response && error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          throw new Error(errorData.message || 'Export failed');
        } catch (parseError) {
          // If parsing fails, throw original error
          throw error;
        }
      }
      throw error;
    }
  }
};

// Report Student List API
export const reportStudentListAPI = {
  getActivities: async (districtCode) => {
    const response = await apiClient.get('/reportstudentlist/activities', { params: { districtCode } });
    return response.data;
  },
  getDistricts: async () => {
    const response = await apiClient.get('/reportstudentlist/districts');
    return response.data;
  },
  getReportData: async (districtCode, activityId) => {
    const response = await apiClient.get('/reportstudentlist/data', { params: { districtCode, activityId } });
    return response.data;
  },
  // Server-side Excel export with ClosedXML
  exportToExcel: async (exportRequest) => {
    const response = await apiClient.post('/reportstudentlist/export-excel', exportRequest, {
      responseType: 'blob' // Important for file download
    });

    // Trigger file download
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Student_List_${exportRequest.districtCode}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  },
  // Server-side PDF export with QuestPDF
  exportToPdf: async (exportRequest) => {
    const response = await apiClient.post('/reportstudentlist/export-pdf', exportRequest, {
      responseType: 'blob' // Important for file download
    });

    // Trigger file download
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Student_List_${exportRequest.districtCode}_${new Date().toISOString().slice(0, 10)}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  }
};

// Report Packing List Shoes API
export const reportPackingListShoesAPI = {
  getActivities: async (districtCode) => {
    const response = await apiClient.get('/reportpackinglistshoes/activities', { params: { districtCode } });
    return response.data;
  },
  getDistricts: async () => {
    const response = await apiClient.get('/reportpackinglistshoes/districts');
    return response.data;
  },
  getReportData: async (districtCode, activityId) => {
    const response = await apiClient.get('/reportpackinglistshoes/data', { params: { districtCode, activityId } });
    return response.data;
  },
  exportToExcel: async (exportRequest) => {
    const response = await apiClient.post('/reportpackinglistshoes/export-excel', exportRequest, {
      responseType: 'blob'
    });

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Packing_List_Shoes_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  }
};

// Report Packing List Shoes By School API
export const reportPackingListShoesBySchoolAPI = {
  getActivities: async (districtCode) => {
    const response = await apiClient.get('/reportpackinglistshoesbyschool/activities', { params: { districtCode } });
    return response.data;
  },
  getDistricts: async (activityId) => {
    const response = await apiClient.get('/reportpackinglistshoesbyschool/districts', { params: { activityId } });
    return response.data;
  },
  getReportData: async (districtCode, activityId) => {
    const response = await apiClient.get('/reportpackinglistshoesbyschool/data', { params: { districtCode, activityId } });
    return response.data;
  },
  exportToExcel: async (exportRequest) => {
    const response = await apiClient.post('/reportpackinglistshoesbyschool/export-excel', exportRequest, {
      responseType: 'blob'
    });

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Packing_List_Shoes_By_School_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  }
};

// Report Packing List Uniform API
export const reportPackingListUniformAPI = {
  getActivities: async (districtCode) => {
    const response = await apiClient.get('/reportpackinglistuniform/activities', { params: { districtCode } });
    return response.data;
  },
  getDistricts: async () => {
    const response = await apiClient.get('/reportpackinglistuniform/districts');
    return response.data;
  },
  getReportData: async (districtCode, activityId) => {
    const response = await apiClient.get('/reportpackinglistuniform/data', { params: { districtCode, activityId } });
    return response.data;
  },
  exportToExcel: async (exportRequest) => {
    const response = await apiClient.post('/reportpackinglistuniform/export-excel', exportRequest, {
      responseType: 'blob'
    });

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Packing_List_Uniform_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  },
  exportPdf: async (exportRequest) => {
    const response = await apiClient.post('/reportpackinglistuniform/export-pdf', exportRequest, {
      responseType: 'blob'
    });
    return response;
  }
};

// Report Packing List Uniform By School API
export const reportPackingListUniformBySchoolAPI = {
  getActivities: async (districtCode) => {
    const response = await apiClient.get('/reportpackinglistuniformbyschool/activities', { params: { districtCode } });
    return response.data;
  },
  getDistricts: async (activityId) => {
    const response = await apiClient.get('/reportpackinglistuniformbyschool/districts', { params: { activityId } });
    return response.data;
  },
  getReportData: async (districtCode, activityId) => {
    const response = await apiClient.get('/reportpackinglistuniformbyschool/data', { params: { districtCode, activityId } });
    return response.data;
  },
  exportToExcel: async (exportRequest) => {
    const response = await apiClient.post('/reportpackinglistuniformbyschool/export-excel', exportRequest, {
      responseType: 'blob'
    });

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Packing_List_Uniform_By_School_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  }
};

// System Audit Log API
export const systemAuditLogAPI = {
  getAuditLogs: async (filters) => {
    const response = await apiClient.get('/auditlog/system', { params: filters });
    return response.data;
  }
};

// Uniform Data Audit Log API
export const uniformDataAuditLogAPI = {
  getAuditLogs: async (filters) => {
    const response = await apiClient.get('/auditlog/uniform-data', { params: filters });
    return response.data;
  }
};

// System Audit Log - Individual function exports (for backward compatibility)
export const getSystemAuditLogs = async (filters) => {
  const response = await apiClient.get('/auditlog/system', { params: filters });
  return response.data;
};

// User Audit Log - Get user account operations (login, logout, user CRUD)
export const getUserAuditLogs = async (filters) => {
  const response = await apiClient.get('/auditlog/user', { params: filters });
  return response.data;
};

export const getActionTypes = async (category) => {
  const params = category ? { category } : {};
  const response = await apiClient.get('/auditlog/action-types', { params });
  return response.data;
};

// Uniform Data Audit Log - Individual function exports (for backward compatibility)
export const getUniformDataAuditLogs = async (filters) => {
  const response = await apiClient.get('/uniformauditlog', { params: filters });
  return response.data;
};

export const getAuditLogSchools = async (module, ppdCode = null) => {
  const params = ppdCode ? { ppdCode } : {};
  const response = await apiClient.get(`/auditlog/${module}/schools`, { params });
  return response.data;
};

export const getAuditLogActivities = async () => {
  const response = await apiClient.get('/auditlog/activities');
  return response.data;
};

export const getStudentUniformDetails = async (schoolCode, year, grade, activityId) => {
  const params = { schoolCode, year, grade, activityId };
  const response = await apiClient.get('/uniformauditlog/student-details', { params });
  return response.data;
};

// Get PPD list for audit log filters
export const getAuditLogPPDList = async () => {
  const response = await apiClient.get('/auditlog/ppd-list');
  return response.data;
};

// Get school list for audit log filters (filtered by module and optionally by PPD)
export const getAuditLogSchoolList = async (module, ppdCode = null) => {
  const params = ppdCode ? { ppdCode } : {};
  const response = await apiClient.get(`/auditlog/${module}/schools`, { params });
  return response.data;
};

// User Profile API
export const userProfileAPI = {
  getProfile: async () => {
    const response = await apiClient.get('/userprofile');
    return response.data;
  },

  updateProfile: async (data) => {
    const response = await apiClient.put('/userprofile', data);
    return response.data;
  },

  changePassword: async (data) => {
    const response = await apiClient.post('/userprofile/change-password', data);
    return response.data;
  }
};

// Student Promotion API
export const studentPromotionAPI = {
  // Get promotion overview by Tahun
  getOverview: async (schoolCode, sourceYear, targetYear) => {
    const response = await apiClient.get('/studentpromotion/overview', {
      params: { schoolCode, sourceYear, targetYear }
    });
    return response.data;
  },

  // Get students for a specific Tahun
  getStudents: async (tahun, schoolCode, sourceYear, targetYear, classFilter = null) => {
    const params = { schoolCode, sourceYear, targetYear };
    if (classFilter) {
      params.classFilter = classFilter;
    }
    const response = await apiClient.get(`/studentpromotion/students/${tahun}`, { params });
    return response.data;
  },

  // Get available classes for the school
  getClasses: async (schoolCode) => {
    const response = await apiClient.get('/studentpromotion/classes', {
      params: { schoolCode }
    });
    return response.data;
  },

  // Process student promotions
  processPromotions: async (request) => {
    const response = await apiClient.post('/studentpromotion/process', request);
    return response.data;
  },

  // Get audit log
  getAuditLog: async (schoolCode, sourceYear, targetYear, tahun = null) => {
    const params = { schoolCode, sourceYear, targetYear };
    if (tahun) {
      params.tahun = tahun;
    }
    const response = await apiClient.get('/studentpromotion/audit-log', { params });
    return response.data;
  }
};

export default apiClient;
